import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, DragOverlay,
  useSensor, useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Sparkles as SparklesIcon, X, Library, Trash2, LayoutTemplate } from "lucide-react";

import api from "@/lib/api";
import { useHistory } from "@/hooks/useHistory";
import { defaultSection, SECTION_LABELS } from "@/components/sections/constants";
import { SectionView } from "@/components/sections/SectionView";
import { BlockPalette } from "@/components/builder/BlockPalette";
import { Canvas } from "@/components/builder/Canvas";
import { PropertiesInspector } from "@/components/builder/PropertiesInspector";
import { BuilderToolbar } from "@/components/builder/BuilderToolbar";
import { TEMPLATE_PRESETS, TEMPLATE_CATEGORIES } from "@/components/builder/templatePresets";
import SeoPanel from "@/components/SeoPanel";

const AI_REWRITE_TONES = ["Professional", "Friendly", "Bold", "Playful", "Luxurious"];

// Font-size CSS scale values applied at the canvas root. Public renderer reads
// the same via inline style so both editor + published page stay in sync.
const FONT_SCALE_MAP = { s: 0.9, m: 1, l: 1.1 };

// Every mutation to `page` flows through commitPage / setPageInstant.
// commitPage: pushes to undo history + triggers debounced save.
// setPageInstant: no history push, no save (used only for local UI-only state
// like when replacing the entire page after a server reload).
export default function LandingPageBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [initialPage, setInitialPage] = useState(null);
  const { state: page, set: setPageInstant, commit: commitPage, undo, redo, canUndo, canRedo } =
    useHistory(initialPage, { max: 50 });

  const [wallet, setWallet] = useState(null);
  const [snippets, setSnippets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null); // { source: 'palette'|'canvas', kind|id }

  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [seoOpen, setSeoOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [tplCategory, setTplCategory] = useState("Hero");

  // AI section rewrite state
  const [aiRewriteSectionId, setAiRewriteSectionId] = useState(null);
  const [aiRewriteTone, setAiRewriteTone] = useState("Professional");
  const [aiRewritePrompt, setAiRewritePrompt] = useState("");
  const [aiRewriteBusy, setAiRewriteBusy] = useState(false);

  // Save-as-template modal state (single section → private snippet)
  const [saveTplSectionId, setSaveTplSectionId] = useState(null);
  const [saveTplName, setSaveTplName] = useState("");
  const [saveTplBusy, setSaveTplBusy] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ---- Load ---------------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: w }, { data: sn }] = await Promise.all([
          api.get(`/landing-pages/${id}`),
          api.get(`/credits/wallet`),
          api.get(`/section-snippets`),
        ]);
        setInitialPage(p);
        setWallet(w);
        setSnippets(sn);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load page");
      }
    })();
  }, [id]);

  // ---- Auto-save (debounced) ----------------------------------------------
  const saveTimerRef = useRef(null);
  const doSave = useCallback(async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/landing-pages/${id}`, patch);
      // Merge server's canonical shape back in without breaking undo history.
      // Only overwrite server-owned fields (created_at, ab_stats, etc.) — not
      // sections/title which the user may have edited since PATCH started.
      setPageInstant((prev) => ({
        ...prev,
        ab_stats: data.ab_stats,
        variant_b: data.variant_b,
        ab_enabled: data.ab_enabled,
        published: data.published,
        updated_at: data.updated_at,
      }));
      setLastSavedAt(Date.now());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [id, setPageInstant]);

  const scheduleSave = useCallback((patch, delay = 600) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { doSave(patch); }, delay);
  }, [doSave]);

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

  // ---- Google Font on-demand loader --------------------------------------
  // When the page's font_family changes, inject a Google Fonts stylesheet if
  // we haven't loaded it yet. Idempotent — safe to run on every render.
  const currentFont = page?.font_family || "Inter";
  useEffect(() => {
    if (!currentFont) return;
    const fam = currentFont.replace(/ /g, "+");
    const id = `gf-${fam}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${fam}:wght@400;600;700;800;900&display=swap`;
    document.head.appendChild(link);
  }, [currentFont]);

  // ---- Mutations ----------------------------------------------------------
  const updateSection = useCallback((nextSection) => {
    commitPage((p) => ({
      ...p,
      sections: p.sections.map((s) => (s.id === nextSection.id ? nextSection : s)),
    }));
    scheduleSave({ sections: replaceById(page.sections, nextSection) });
  }, [commitPage, page, scheduleSave]);

  const updatePage = useCallback((patch) => {
    commitPage((p) => ({ ...p, ...patch }));
    scheduleSave(patch);
  }, [commitPage, scheduleSave]);

  const insertAt = useCallback((kind, index) => {
    const fresh = defaultSection(kind);
    commitPage((p) => {
      const next = [...p.sections];
      next.splice(index, 0, fresh);
      return { ...p, sections: next };
    });
    // Compute the new list synchronously for the save payload (avoid stale ref)
    const newList = (() => {
      const next = [...(page.sections || [])];
      next.splice(index, 0, fresh);
      return next;
    })();
    scheduleSave({ sections: newList }, 200);
    setSelectedId(fresh.id);
  }, [commitPage, page, scheduleSave]);

  const appendSection = useCallback((kind) => {
    insertAt(kind, page?.sections?.length || 0);
  }, [insertAt, page]);

  const deleteSection = useCallback((sid) => {
    const target = page.sections.find((s) => s.id === sid);
    if (!target) return;
    if (!window.confirm(`Delete "${SECTION_LABELS[target.kind] || target.kind}" section?`)) return;
    commitPage((p) => ({ ...p, sections: p.sections.filter((s) => s.id !== sid) }));
    scheduleSave({ sections: page.sections.filter((s) => s.id !== sid) }, 200);
    if (selectedId === sid) setSelectedId(null);
  }, [commitPage, page, scheduleSave, selectedId]);

  const duplicateSection = useCallback((sid) => {
    const idx = page.sections.findIndex((s) => s.id === sid);
    if (idx < 0) return;
    const clone = { ...page.sections[idx], id: Math.random().toString(36).slice(2, 8) };
    commitPage((p) => {
      const next = [...p.sections];
      next.splice(idx + 1, 0, clone);
      return { ...p, sections: next };
    });
    const newList = (() => {
      const next = [...page.sections];
      next.splice(idx + 1, 0, clone);
      return next;
    })();
    scheduleSave({ sections: newList }, 200);
    setSelectedId(clone.id);
  }, [commitPage, page, scheduleSave]);

  const moveSection = useCallback((sid, newIdx) => {
    const oldIdx = page.sections.findIndex((s) => s.id === sid);
    if (oldIdx < 0 || newIdx < 0 || newIdx >= page.sections.length) return;
    const reordered = arrayMove(page.sections, oldIdx, newIdx);
    commitPage((p) => ({ ...p, sections: reordered }));
    scheduleSave({ sections: reordered }, 200);
  }, [commitPage, page, scheduleSave]);

  // Inline text edit fired from Canvas dblclick → save one field of a section.
  const inlineEditField = useCallback((sid, field, value) => {
    const idx = page.sections.findIndex((s) => s.id === sid);
    if (idx < 0) return;
    const next = { ...page.sections[idx], [field]: value };
    const nextList = [...page.sections];
    nextList[idx] = next;
    commitPage((p) => ({ ...p, sections: nextList }));
    scheduleSave({ sections: nextList }, 300);
  }, [commitPage, page, scheduleSave]);

  // AI rewrite one section (opens modal — actual API call in confirmAiRewrite)
  const openAiRewrite = useCallback((sid) => {
    setAiRewriteSectionId(sid);
    setAiRewriteTone("Professional");
    setAiRewritePrompt("");
  }, []);

  const confirmAiRewrite = async () => {
    if (!aiRewriteSectionId) return;
    setAiRewriteBusy(true);
    try {
      const { data } = await api.post(
        `/landing-pages/${id}/sections/${aiRewriteSectionId}/rewrite`,
        { tone: aiRewriteTone.toLowerCase(), custom_prompt: aiRewritePrompt || undefined },
      );
      if (!data.ok) { toast.error(data.message || "Try again"); return; }
      // Replace section in-place (goes through history so user can undo)
      commitPage((p) => ({
        ...p,
        sections: p.sections.map((s) => (s.id === aiRewriteSectionId ? data.section : s)),
      }));
      const { data: w } = await api.get("/credits/wallet"); setWallet(w);
      toast.success(`Section rewritten · -${data.credits_charged} credit`);
      setAiRewriteSectionId(null);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(e.response?.status === 402 ? msg + " · Buy credits →" : msg);
    } finally { setAiRewriteBusy(false); }
  };

  // Insert one preset (either a single section OR an array of sections)
  const insertPreset = useCallback((preset) => {
    const built = preset.build();
    const toAdd = Array.isArray(built) ? built : [built];
    commitPage((p) => ({ ...p, sections: [...(p.sections || []), ...toAdd] }));
    const newList = [...(page.sections || []), ...toAdd];
    scheduleSave({ sections: newList }, 200);
    setTemplatesOpen(false);
    setSelectedId(toAdd[0].id);
    toast.success(`Inserted "${preset.name}"`);
  }, [commitPage, page, scheduleSave]);

  // Save section as a reusable snippet (private to this workspace).
  const openSaveTemplate = useCallback((sid) => {
    const sec = page.sections.find((s) => s.id === sid);
    if (!sec) return;
    setSaveTplSectionId(sid);
    setSaveTplName(sec.title || SECTION_LABELS[sec.kind] || "My section");
  }, [page]);

  const confirmSaveTemplate = async () => {
    if (!saveTplSectionId) return;
    const name = saveTplName.trim();
    if (name.length < 2) { toast.error("Name it something memorable"); return; }
    const sec = page.sections.find((s) => s.id === saveTplSectionId);
    if (!sec) { setSaveTplSectionId(null); return; }
    setSaveTplBusy(true);
    try {
      // Strip transient fields (id + ai_history) — the snippet is a template
      // that will be given a fresh id on insert into another page.
      const { id: _skip, ai_history: _drop, ...body } = sec;
      const { data } = await api.post("/section-snippets", { name, section: body });
      setSnippets((prev) => [data, ...prev.filter((x) => x.id !== data.id)]);
      toast.success(`Saved "${name}" to your library`);
      setSaveTplSectionId(null); setSaveTplName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaveTplBusy(false); }
  };

  // AI rewrite history: restore a specific past version of a section.
  const restoreAiVersion = async (sid, idx) => {
    try {
      const { data } = await api.post(`/landing-pages/${id}/sections/${sid}/restore/${idx}`);
      if (!data.ok) { toast.error("Restore failed"); return; }
      commitPage((p) => ({
        ...p,
        sections: p.sections.map((s) => (s.id === sid ? data.section : s)),
      }));
      toast.success("Restored earlier version");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ---- DnD handlers -------------------------------------------------------
  const onDragStart = (e) => {
    setActiveDrag({
      id: e.active.id,
      kind: e.active.data.current?.kind,
      source: e.active.data.current?.source,
    });
  };

  const onDragEnd = (e) => {
    const active = e.active;
    const over = e.over;
    setActiveDrag(null);
    if (!over) return;

    const src = active.data.current?.source;

    if (src === "palette") {
      // Dropped a fresh block from palette
      const kind = active.data.current?.kind;
      const overId = String(over.id);
      let insertIdx = page.sections.length;
      if (overId.startsWith("dropzone:")) {
        insertIdx = parseInt(overId.slice("dropzone:".length), 10);
      } else {
        // Dropped over an existing section — insert AFTER it
        const idx = page.sections.findIndex((s) => s.id === overId);
        if (idx >= 0) insertIdx = idx + 1;
      }
      insertAt(kind, insertIdx);
      return;
    }

    if (src === "canvas" && active.id !== over.id) {
      const oldIdx = page.sections.findIndex((s) => s.id === active.id);
      const newIdx = page.sections.findIndex((s) => s.id === over.id);
      if (oldIdx >= 0 && newIdx >= 0) {
        const reordered = arrayMove(page.sections, oldIdx, newIdx);
        commitPage((p) => ({ ...p, sections: reordered }));
        scheduleSave({ sections: reordered }, 200);
      }
    }
  };

  // ---- Keyboard shortcuts -------------------------------------------------
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "z" && !e.shiftKey) {
        // Don't hijack Cmd-Z inside an input/textarea/contenteditable
        const t = e.target;
        const tag = (t?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
        e.preventDefault();
        const restored = undo();
        if (restored?.sections) scheduleSave({ sections: restored.sections }, 200);
      } else if (mod && (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey))) {
        const t = e.target;
        const tag = (t?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
        e.preventDefault();
        const restored = redo();
        if (restored?.sections) scheduleSave({ sections: restored.sections }, 200);
      } else if (e.key === "Escape") {
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, scheduleSave]);

  // ---- Top-level actions --------------------------------------------------
  const publishToggle = async () => {
    try {
      const { data } = await api.patch(`/landing-pages/${id}`, { published: !page.published });
      setPageInstant((p) => ({ ...p, published: data.published }));
      toast.success(data.published ? "Published live" : "Unpublished");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createVariant = async () => {
    try {
      const { data } = await api.post(`/landing-pages/${id}/variant`, { name: "Variant B" });
      setPageInstant(data);
      toast.success("Variant B created · 50/50 split");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleAb = async () => {
    try {
      const { data } = await api.post(`/landing-pages/${id}/variant/toggle`);
      setPageInstant((p) => ({ ...p, ab_enabled: data.ab_enabled }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteVariant = async () => {
    if (!window.confirm("Delete Variant B? Stats lost.")) return;
    try {
      await api.delete(`/landing-pages/${id}/variant`);
      setPageInstant((p) => ({ ...p, variant_b: null, ab_enabled: false, ab_stats: null }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runAi = async () => {
    if (aiPrompt.trim().length < 10) { toast.error("Give me 10+ chars of detail"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post(`/landing-pages/${id}/generate-ai`, { prompt: aiPrompt });
      if (!data.ok) { toast.error(data.message || "Try again"); }
      else {
        setInitialPage(data.page); // reset history to server truth
        toast.success(`AI generated ${data.page.sections.length} sections`);
        setAiOpen(false); setAiPrompt("");
        const { data: w } = await api.get("/credits/wallet"); setWallet(w);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(e.response?.status === 402 ? msg + " · Buy credits →" : msg);
    } finally { setAiBusy(false); }
  };

  const insertSnippet = (snip) => {
    const clone = { ...snip.section, id: Math.random().toString(36).slice(2, 8) };
    commitPage((p) => ({ ...p, sections: [...p.sections, clone] }));
    const newList = [...page.sections, clone];
    scheduleSave({ sections: newList }, 200);
    setLibOpen(false);
    setSelectedId(clone.id);
    toast.success("Section inserted");
  };

  const deleteSnippet = async (sid) => {
    if (!window.confirm("Delete this saved section?")) return;
    try {
      await api.delete(`/section-snippets/${sid}`);
      setSnippets((s) => s.filter((x) => x.id !== sid));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ---- Render -------------------------------------------------------------
  if (!page) {
    return <div className="min-h-screen flex items-center justify-center text-[#4A4D57]">Loading builder…</div>;
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-white" data-testid="landing-builder">
      <BuilderToolbar
        page={page}
        wallet={wallet}
        saving={saving}
        lastSavedAt={lastSavedAt}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={() => { const r = undo(); if (r?.sections) scheduleSave({ sections: r.sections }, 200); }}
        onRedo={() => { const r = redo(); if (r?.sections) scheduleSave({ sections: r.sections }, 200); }}
        device={device}
        onDeviceChange={setDevice}
        onAiOpen={() => setAiOpen(true)}
        onPreview={() => window.open(`/p/${page.slug}`, "_blank", "noopener")}
        onPublish={publishToggle}
        onWaitlist={() => navigate(`/app/pages/${id}/waitlist`)}
        onCreateVariant={createVariant}
        onToggleAb={toggleAb}
        onDeleteVariant={deleteVariant}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveDrag(null)}
      >
      <div
        className="flex-1 flex min-h-0"
        style={{
          fontFamily: `"${currentFont}", system-ui, sans-serif`,
          fontSize: `${(FONT_SCALE_MAP[page.font_scale || "m"]) * 16}px`,
        }}
      >
          <BlockPalette
            onAdd={appendSection}
            onOpenLibrary={() => setLibOpen(true)}
            onOpenTemplates={() => setTemplatesOpen(true)}
            snippetCount={snippets.length}
          />

          <Canvas
            sections={page.sections || []}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onDelete={deleteSection}
            onDuplicate={duplicateSection}
            onMove={moveSection}
            onAiRewrite={openAiRewrite}
            onSaveAsTemplate={openSaveTemplate}
            onInlineEdit={inlineEditField}
            onDeselect={() => setSelectedId(null)}
            device={device}
            accent={page.accent_color || "#0B5CFF"}
            activePaletteKind={activeDrag?.source === "palette" ? activeDrag.kind : null}
          />

          <PropertiesInspector
            page={page}
            selectedId={selectedId}
            onChange={updateSection}
            onPageChange={updatePage}
            onDeselect={() => setSelectedId(null)}
            onDelete={() => selectedId && deleteSection(selectedId)}
            onDuplicate={() => selectedId && duplicateSection(selectedId)}
            onSaveAsTemplate={() => selectedId && openSaveTemplate(selectedId)}
            onRestoreAiVersion={(idx) => selectedId && restoreAiVersion(selectedId, idx)}
            onOpenSeo={() => setSeoOpen(true)}
          />
        </div>

        <DragOverlay dropAnimation={null}>
          {activeDrag?.source === "palette" && (
            <div className="rounded-xl border-2 border-[#0B5CFF] bg-white p-3 shadow-2xl w-56 pointer-events-none">
              <div className="font-semibold text-sm text-[#0B0D17]">{SECTION_LABELS[activeDrag.kind]}</div>
              <div className="text-[11px] text-[#0B5CFF] mt-0.5">Drop to insert</div>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* AI modal */}
      {aiOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-6" onClick={() => !aiBusy && setAiOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => !aiBusy && setAiOpen(false)} className="absolute top-3 right-3 p-1.5 hover:bg-[#F1F3F7] rounded-md" data-testid="ai-modal-close">
              <X className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <SparklesIcon className="w-5 h-5 text-[#0B5CFF]" strokeWidth={1.75} />
              <h2 className="font-display font-bold text-xl">AI generate landing page</h2>
            </div>
            <p className="text-sm text-[#4A4D57]">Describe who you help, what you sell, and the tone. AI drafts a full 4-6 section stack.</p>
            <textarea
              rows={5}
              className="vp-input mt-4 text-sm"
              placeholder="e.g. Landing page for a coach who helps young pros invest their first crore. Calm, trustworthy tone."
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              data-testid="ai-prompt-input"
            />
            <div className="mt-3 text-xs text-[#747680]">Cost: <b className="text-[#0B0D17]">5 credits</b> · Balance: <b className="text-[#0B0D17]">{wallet?.balance ?? 0}</b></div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setAiOpen(false)} disabled={aiBusy} className="vp-btn-ghost text-sm">Cancel</button>
              <button onClick={runAi} disabled={aiBusy} className="vp-btn-primary text-sm" data-testid="ai-generate-btn">
                {aiBusy ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section library modal */}
      {libOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-6" onClick={() => setLibOpen(false)}>
          <div className="w-full max-w-2xl max-h-[80vh] overflow-auto rounded-2xl bg-white p-6 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLibOpen(false)} className="absolute top-3 right-3 p-1.5 hover:bg-[#F1F3F7] rounded-md" data-testid="library-modal-close">
              <X className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <Library className="w-5 h-5 text-[#0B5CFF]" strokeWidth={1.75} />
              <h2 className="font-display font-bold text-xl">Section library</h2>
            </div>
            <p className="text-sm text-[#4A4D57] mb-4">Reusable blocks you've saved. Click to insert into this page.</p>
            {snippets.length === 0 ? (
              <div className="text-sm text-[#747680] text-center py-10">No saved sections yet.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {snippets.map((s) => (
                  <div key={s.id} className="rounded-xl border border-[#E4E5E9] p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-semibold text-sm">{s.name}</div>
                        <div className="text-xs text-[#747680] mt-0.5">{SECTION_LABELS[s.section?.kind] || s.section?.kind}</div>
                      </div>
                      <button onClick={() => deleteSnippet(s.id)} className="p-1 text-[#4A4D57] hover:text-[#B00020]" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                    <button onClick={() => insertSnippet(s)} className="mt-3 vp-btn-primary text-xs w-full py-1.5" data-testid={`insert-snip-${s.id}`}>
                      Insert into page
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SEO modal */}
      {seoOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-6" onClick={() => setSeoOpen(false)}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-auto rounded-2xl bg-white p-6 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSeoOpen(false)} className="absolute top-3 right-3 p-1.5 hover:bg-[#F1F3F7] rounded-md" data-testid="seo-modal-close">
              <X className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <h2 className="font-display font-bold text-xl mb-4">SEO & social preview</h2>
            <SeoPanel
              page={page}
              onChange={(patch) => updatePage(patch)}
              siteHost={typeof window !== "undefined" ? window.location.host : "videoepreneur.com"}
            />
          </div>
        </div>
      )}

      {/* Templates marketplace modal */}
      {templatesOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-6" onClick={() => setTemplatesOpen(false)}>
          <div className="w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-2xl bg-white shadow-2xl relative flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4E5E9]">
              <div className="flex items-center gap-2">
                <LayoutTemplate className="w-5 h-5 text-[#0B5CFF]" strokeWidth={1.75} />
                <h2 className="font-display font-bold text-xl">Section templates</h2>
                <span className="text-xs text-[#747680] ml-2">Click any preset to insert it into your page.</span>
              </div>
              <button onClick={() => setTemplatesOpen(false)} className="p-1.5 hover:bg-[#F1F3F7] rounded-md" data-testid="templates-modal-close">
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
            {/* Category chips */}
            <div className="px-6 py-3 border-b border-[#E4E5E9] flex items-center gap-1.5 overflow-x-auto">
              {TEMPLATE_CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setTplCategory(c)}
                  className={"h-7 px-3 rounded-full text-xs font-semibold whitespace-nowrap " +
                    (tplCategory === c
                      ? "bg-[#0B5CFF] text-white"
                      : "bg-[#F1F3F7] text-[#0B0D17] hover:bg-[#E4E5E9]")}
                  data-testid={`tpl-cat-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
            {/* Grid */}
            <div className="flex-1 overflow-y-auto p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {TEMPLATE_PRESETS.filter((t) => t.category === tplCategory).map((t) => (
                <TemplatePresetCard key={t.id} preset={t} onInsert={insertPreset} />
              ))}
              {TEMPLATE_PRESETS.filter((t) => t.category === tplCategory).length === 0 && (
                <div className="col-span-full text-center py-12 text-sm text-[#747680]">No templates in this category yet.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save-as-template modal */}
      {saveTplSectionId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-6" onClick={() => !saveTplBusy && setSaveTplSectionId(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => !saveTplBusy && setSaveTplSectionId(null)} className="absolute top-3 right-3 p-1.5 hover:bg-[#F1F3F7] rounded-md" data-testid="save-tpl-close">
              <X className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <h2 className="font-display font-bold text-xl mb-1">Save as template</h2>
            <p className="text-sm text-[#4A4D57] mb-4">Reuse this section on future landing pages in one click.</p>
            <label className="block">
              <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Template name</span>
              <input
                type="text"
                autoFocus
                value={saveTplName}
                onChange={(e) => setSaveTplName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") confirmSaveTemplate(); }}
                placeholder="e.g. Coach hero — bold red"
                className="vp-input text-sm"
                data-testid="save-tpl-name"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setSaveTplSectionId(null)} disabled={saveTplBusy} className="vp-btn-ghost text-sm">Cancel</button>
              <button onClick={confirmSaveTemplate} disabled={saveTplBusy} className="vp-btn-primary text-sm" data-testid="save-tpl-confirm">
                {saveTplBusy ? "Saving…" : "Save to library"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI section rewrite modal */}
      {aiRewriteSectionId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-6" onClick={() => !aiRewriteBusy && setAiRewriteSectionId(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => !aiRewriteBusy && setAiRewriteSectionId(null)} className="absolute top-3 right-3 p-1.5 hover:bg-[#F1F3F7] rounded-md" data-testid="ai-rewrite-close">
              <X className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <SparklesIcon className="w-5 h-5 text-[#0B5CFF]" strokeWidth={1.75} />
              <h2 className="font-display font-bold text-xl">Rewrite section with AI</h2>
            </div>
            <p className="text-sm text-[#4A4D57]">Only this section's copy will change. Images, colors, and structure are preserved.</p>

            <div className="mt-4">
              <div className="vp-label mb-2" style={{ fontSize: "0.65rem" }}>Tone</div>
              <div className="flex flex-wrap gap-1.5" data-testid="ai-rewrite-tone-group">
                {AI_REWRITE_TONES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setAiRewriteTone(t)}
                    className={"h-8 px-3 rounded-full text-xs font-semibold " +
                      (aiRewriteTone === t
                        ? "bg-[#0B5CFF] text-white"
                        : "bg-[#F1F3F7] text-[#0B0D17] hover:bg-[#E4E5E9]")}
                    data-testid={`ai-rewrite-tone-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <label className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Custom instructions (optional)</label>
              <textarea
                rows={3}
                className="vp-input text-sm"
                placeholder="e.g. Focus on urgency, keep it under 40 words per paragraph."
                value={aiRewritePrompt}
                onChange={(e) => setAiRewritePrompt(e.target.value)}
                data-testid="ai-rewrite-prompt"
              />
            </div>

            <div className="mt-3 text-xs text-[#747680]">Cost: <b className="text-[#0B0D17]">1 credit</b> · Balance: <b className="text-[#0B0D17]">{wallet?.balance ?? 0}</b></div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setAiRewriteSectionId(null)} disabled={aiRewriteBusy} className="vp-btn-ghost text-sm">Cancel</button>
              <button onClick={confirmAiRewrite} disabled={aiRewriteBusy} className="vp-btn-primary text-sm" data-testid="ai-rewrite-confirm">
                {aiRewriteBusy ? "Rewriting…" : "Rewrite"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper: return a new list with a section replaced by id. Used inside
// save-payload preparation so we're never persisting stale state.
function replaceById(list, next) {
  return (list || []).map((s) => (s.id === next.id ? next : s));
}

// Template card with a lazy-rendered live preview on hover. The preset only
// gets `.build()`-materialised the first time the user hovers, so opening the
// modal stays instant even with 30+ presets. Rendered inside a scaled iframe-
// like container to avoid its styles leaking into the modal layout.
function TemplatePresetCard({ preset, onInsert }) {
  const [hovering, setHovering] = useState(false);
  // Build once, memo — no need to rebuild every render.
  const built = useMemo(() => (hovering ? preset.build() : null), [hovering, preset]);
  const previewSections = built ? (Array.isArray(built) ? built : [built]) : [];

  return (
    <button
      onClick={() => onInsert(preset)}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className="text-left rounded-xl border border-[#E4E5E9] bg-white p-3 hover:border-[#0B5CFF] hover:shadow-md transition-all group"
      data-testid={`tpl-${preset.id}`}
    >
      <div className="relative w-full h-32 rounded-lg mb-3 overflow-hidden" style={{ background: preset.thumb }}>
        {/* Live preview overlay — appears on hover. Rendered at ~30% scale so a
            hero, features, etc. all fit in the card thumbnail. */}
        {hovering && previewSections.length > 0 && (
          <div
            className="absolute inset-0 bg-white overflow-hidden pointer-events-none"
            data-testid={`tpl-preview-${preset.id}`}
          >
            <div
              className="origin-top-left"
              style={{ transform: "scale(0.28)", width: `${100 / 0.28}%`, height: `${100 / 0.28}%` }}
            >
              {previewSections.map((sec) => (
                <SectionView key={sec.id} section={sec} accent="#0B5CFF" />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="font-semibold text-sm text-[#0B0D17]">{preset.name}</div>
      <div className="text-[11px] text-[#747680] mt-0.5">{preset.vibe}</div>
    </button>
  );
}
